import { Footer } from "@web/components/footers/Footer";
import { useSetBodyBgColor } from "@web/hooks/useSetBodyBgColor";
import { useSetBodyOverflowX } from "@web/hooks/useSetBodyOverflowX";
import { AboutContainer } from "@web/pages/about/AboutContainer";
import { AboutHeader } from "@web/pages/about/AboutHeader";
import { AboutParagraph } from "@web/pages/about/AboutParagraph";
import { ColorClass, ColorValue, joinClasses } from "ui";
import styles from "./css/AboutPage.module.css";

type PersonalCardProps = {
  description: string;
  imgSrc: string;
  link: string;
  linkReadable: string;
  name: string;
};

function PersonalCard({
  description,
  imgSrc,
  link,
  linkReadable,
  name,
}: PersonalCardProps) {
  return (
    <div className={styles.personalCard}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img className={styles.personalImage} src={imgSrc} alt="Personal" />
      <div className={styles.personalInfo}>
        <div className={joinClasses(styles.personalInfoName)}>{name}</div>
        <div
          className={joinClasses(
            styles.personalInfoDescription,
            styles.personalInfoText
          )}
        >
          {description}
        </div>
        <a
          className={joinClasses(
            styles.personalInfoLink,
            styles.personalInfoText,
            ColorClass.Secondary
          )}
          href={link}
          rel="noreferrer"
          target="_blank"
        >
          {linkReadable}
        </a>
      </div>
    </div>
  );
}

function SummarySection() {
  return (
    <div>
      <AboutParagraph>
        We started Harken because everyone has trouble remembering what they
        learn.
      </AboutParagraph>
      <AboutParagraph>
        Most people who read books or take online classes either accept that
        they&apos;ll forget, or they spend even more time and money relearning
        what they forgot. That applies to us, too—as lifelong learners,
        we&apos;ve experienced this firsthand, and it&apos;s super frustrating.
      </AboutParagraph>
      <AboutParagraph style={{ fontWeight: 600 }}>
        With Harken, we&apos;re making this a problem of the past.
      </AboutParagraph>
    </div>
  );
}

function Title() {
  return (
    <>
      <div className={joinClasses(styles.title)}>
        Our mission is to help people remember the things they care about.
      </div>
      <div className={styles.purpleSquiggly}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/images/about/purple-squiggly.svg" alt="Separator" />
      </div>
    </>
  );
}

function _WhatWeBuildingSection() {
  return (
    <>
      <div className={joinClasses(styles.sectionTitle)}>
        What We&apos;re Building
      </div>
      <div>
        <AboutParagraph>
          We&apos;re building Harken in two parts. First, we&apos;re making the
          flashcard app we wish existed. It makes spaced repetition accessible
          with a powerful yet approachable system, and the explore page makes it
          easy to get started without writing a single card.
        </AboutParagraph>
        <AboutParagraph>
          Next, we&apos;ll build our online class platform—you can think of it
          as Udemy and Anki combined. When you take a Harken class, your
          knowledge will be tested by embedded flashcards and preserved by
          reviewing the cards on a spaced repetition schedule. If you take one
          of our classes, you&apos;ll never forget it.
        </AboutParagraph>
      </div>
    </>
  );
}

function WhoWeAreSection() {
  return (
    <>
      <div className={joinClasses(styles.sectionTitle)}>Who We Are</div>
      <div>
        <AboutParagraph>
          We&apos;re both makers and lifelong learners, excited about helping
          people remember what they care about &#128155;
        </AboutParagraph>
        <AboutParagraph>
          If you have feedback for us about Harken, feel free to reach out{" "}
          <a className={styles.link} href="https://twitter.com/pencilflip">
            on Twitter
          </a>
          !
        </AboutParagraph>
      </div>
      <div className={styles.personalCardContainer1}>
        <PersonalCard
          description="Engineer, blogger, mushroom enthusiast"
          imgSrc="/images/about/matt.png"
          link="https://mattlim.me"
          linkReadable="mattlim.me"
          name="Matt Lim"
        />
      </div>
      <div className={styles.personalCardContainer2}>
        <PersonalCard
          description="Designer, illustrator, dessert monster"
          imgSrc="/images/about/katherine.png"
          link="https://katherineliu.me"
          linkReadable="katherineliu.me"
          name="Katherine Liu"
        />
      </div>
    </>
  );
}

export function AboutPage() {
  useSetBodyBgColor(ColorValue.WebsiteBackground);
  // Otherwise, the 100vw will cause there to be a small overflow-x
  // to see beneath the vertical scroll bar.
  useSetBodyOverflowX("hidden");

  return (
    <AboutContainer>
      <AboutHeader />
      <Title />
      <div className={styles.body}>
        <SummarySection />
        {/* <WhatWeBuildingSection /> */}
        <WhoWeAreSection />
      </div>
      <div style={{ width: "100vw" }}>
        <Footer />
      </div>
    </AboutContainer>
  );
}
